import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, FileText, Calendar, Plus } from 'lucide-react';
import { useDeals } from '../../store/DealsContext';
import { useCompliance } from '../../hooks/useCompliance';
import { formatDate } from '../../utils/formatters';
import ComplianceForm from './ComplianceForm';
import ComplianceList from './ComplianceList';
import ComplianceDetails from './ComplianceDetails';
import { ComplianceRequirement } from '../../types/compliance';

export default function ComplianceTracker() {
  const { deals } = useDeals();
  const [selectedDeal, setSelectedDeal] = useState<string>('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState<ComplianceRequirement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');

  const {
    requirements,
    metrics,
    addRequirement,
    updateRequirement,
    deleteRequirement
  } = useCompliance(selectedDeal);

  const filteredRequirements = requirements.filter(req => {
    const matchesSearch = req.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || req.category === filterCategory;
    const matchesPriority = !filterPriority || req.priority === filterPriority;
    return matchesSearch && matchesCategory && matchesPriority;
  });

  return (
    <div className="h-full space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Compliance Tracker</h2>
          <p className="mt-2 text-gray-600">Monitor regulatory requirements and filings</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedDeal}
            onChange={(e) => setSelectedDeal(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Deals</option>
            {deals.map((deal) => (
              <option key={deal.id} value={deal.id}>{deal.name}</option>
            ))}
          </select>

          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Requirement
          </button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Total Requirements</h3>
            <FileText className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{metrics.total}</p>
          <p className="mt-1 text-sm text-gray-500">Across all categories</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Completed</h3>
            <CheckCircle2 className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{metrics.completed}</p>
          <p className="mt-1 text-sm text-gray-500">Requirements met</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Overdue</h3>
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{metrics.overdue}</p>
          <p className="mt-1 text-sm text-gray-500">Past due date</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Upcoming</h3>
            <Calendar className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {requirements.filter(r => 
              r.status !== 'completed' && 
              new Date(r.dueDate) > new Date() &&
              new Date(r.dueDate) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
            ).length}
          </p>
          <p className="mt-1 text-sm text-gray-500">Due in 7 days</p>
        </div>
      </div>

      {/* Requirements List */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search requirements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Categories</option>
              <option value="regulatory">Regulatory</option>
              <option value="antitrust">Antitrust</option>
              <option value="financial">Financial</option>
              <option value="legal">Legal</option>
            </select>

            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        <ComplianceList
          requirements={filteredRequirements}
          onStatusChange={(id) => {
            const requirement = requirements.find(r => r.id === id);
            if (requirement) {
              updateRequirement(id, {
                status: requirement.status === 'completed' ? 'pending' : 'completed'
              });
            }
          }}
          onDelete={deleteRequirement}
          onViewDetails={(id) => {
            const requirement = requirements.find(r => r.id === id);
            if (requirement) {
              setSelectedRequirement(requirement);
            }
          }}
        />
      </div>

      {showAddForm && (
        <ComplianceForm
          onClose={() => setShowAddForm(false)}
          onSubmit={addRequirement}
          deals={deals}
        />
      )}

      {selectedRequirement && (
        <ComplianceDetails
          requirement={selectedRequirement}
          onClose={() => setSelectedRequirement(null)}
          onUpdate={updateRequirement}
        />
      )}
    </div>
  );
}